import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { ChevronLeft } from "lucide-react";
import { Api, netflixToLibraryItem, curiosityToLibraryItem, type LibraryItem } from "../lib/api.js";
import { linkForItem } from "../lib/linkForItem.js";

/**
 * Service drill-in (/service/:key) — the browse page behind a "Hot on
 * <service>" strip.
 *
 *   curiosity        → CuriosityStream: categories from the local catalog
 *                      mirror, then items per category (plays directly).
 *   apple|hbo|cable|nfb → TMDB-discover catalogs, paged (play via Real-Debrid).
 */

const PROVIDER_KEYS = new Set(["apple", "hbo", "cable", "nfb"] as const);
type ProviderKey = "apple" | "hbo" | "cable" | "nfb";

export function Service(): JSX.Element {
  const { key = "" } = useParams();
  if (key === "curiosity") return <CuriosityBrowse />;
  if (PROVIDER_KEYS.has(key as ProviderKey)) return <ProviderBrowse provider={key as ProviderKey} />;
  return (
    <div>
      <h1 className="font-display text-3xl text-ivory">Unknown service</h1>
      <div className="ornament-rule" />
      <p className="text-parchment text-sm">
        No drill-in for <code>{key}</code>. <Link to="/" className="text-gold hover:underline">Back home</Link>
      </p>
    </div>
  );
}

// ── TMDB-discover services (apple / hbo / cable / nfb) ──────────────────────

function ProviderBrowse({ provider }: { provider: ProviderKey }): JSX.Element {
  const q = useInfiniteQuery({
    queryKey: ["provider-browse", provider],
    queryFn: ({ pageParam }) => Api.library.providerBrowse(provider, pageParam),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.hasMore ? last.page + 1 : undefined),
    staleTime: 3 * 60 * 60_000,
  });
  const label = q.data?.pages[0]?.label ?? provider;
  const items = (q.data?.pages ?? []).flatMap((p) => p.items);
  return (
    <div>
      <BrowseHeader title={label} caption="TMDB catalog · stream via Real-Debrid" />
      {items.length === 0 && !q.isLoading && (
        <p className="text-parchment text-sm">Nothing found for this service.</p>
      )}
      <PosterGrid items={items.map(netflixToLibraryItem)} />
      <div className="mt-6 flex items-center gap-3">
        {q.hasNextPage && (
          <button className="pill" disabled={q.isFetchingNextPage} onClick={() => void q.fetchNextPage()}>
            {q.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        )}
        {q.isLoading && <span className="text-parchment/60 text-sm">Loading…</span>}
      </div>
    </div>
  );
}

// ── CuriosityStream ─────────────────────────────────────────────────────────

function CuriosityBrowse(): JSX.Element {
  const [category, setCategory] = useState<string | null>(null);
  const catsQ = useQuery({
    queryKey: ["curiosity-categories"],
    queryFn: Api.library.curiosityCategories,
    staleTime: 6 * 60 * 60_000,
    retry: false,
  });
  const itemsQ = useQuery({
    queryKey: ["curiosity-category", category],
    queryFn: () => Api.library.curiosityCategory(category!),
    enabled: category !== null,
    staleTime: 6 * 60 * 60_000,
    retry: false,
  });

  const unavailable = catsQ.isError || itemsQ.isError;

  if (category !== null) {
    const items = itemsQ.data?.items ?? [];
    return (
      <div>
        <BrowseHeader
          title={`CuriosityStream — ${category}`}
          caption="plays directly with your CuriosityStream account"
          back={() => setCategory(null)}
        />
        {itemsQ.isLoading && <p className="text-parchment/60 text-sm">Loading…</p>}
        <PosterGrid items={items.map(curiosityToLibraryItem)} />
      </div>
    );
  }

  const cats = catsQ.data?.categories ?? [];
  return (
    <div>
      <BrowseHeader title="CuriosityStream" caption="pick a category · plays directly with your account" />
      {unavailable && (
        <p className="text-parchment text-sm">
          CuriosityStream isn't reachable right now (account credentials in Sources may need attention).
        </p>
      )}
      {catsQ.isLoading && <p className="text-parchment/60 text-sm">Loading…</p>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {cats.map((c) => (
          <button
            key={c.name}
            onClick={() => setCategory(c.name)}
            className="card p-5 text-left hover:scale-[1.03] transition-transform duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi"
          >
            <div className="font-display text-ivory text-base">{c.name}</div>
            <div className="text-parchment/60 text-xs mt-1">{c.count} title{c.count === 1 ? "" : "s"}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

// ── shared bits ─────────────────────────────────────────────────────────────

function BrowseHeader({ title, caption, back }: { title: string; caption: string; back?: () => void }): JSX.Element {
  return (
    <>
      <div className="flex items-baseline justify-between">
        <div className="flex items-center gap-3">
          {back && (
            <button className="pill-ghost p-2" onClick={back} aria-label="back to categories">
              <ChevronLeft size={16} />
            </button>
          )}
          <h1 className="font-display text-3xl text-ivory">{title}</h1>
        </div>
        <span className="section-label">{caption}</span>
      </div>
      <div className="ornament-rule" />
    </>
  );
}

function PosterGrid({ items }: { items: LibraryItem[] }): JSX.Element {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-4">
      {items.map((it) => (
        <Link
          key={it.id}
          to={linkForItem({ id: it.id, kind: it.kind, category: it.category })}
          className="card group overflow-hidden hover:scale-[1.04] transition-transform duration-200 ease-spring focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-goldHi"
        >
          <div className="aspect-[2/3] bg-elev relative">
            {it.thumbnail_url ? (
              <img
                src={it.thumbnail_url}
                alt=""
                loading="lazy"
                referrerPolicy="no-referrer"
                className="w-full h-full object-cover"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center font-display text-parchment/40 text-2xl">
                {it.title.slice(0, 1)}
              </div>
            )}
          </div>
          <div className="p-2">
            <div className="text-ivory text-xs leading-snug line-clamp-2">{it.title}</div>
          </div>
        </Link>
      ))}
    </div>
  );
}
